import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Alert } from '../models/alerts';
import { dismissAlertId, evictDismissedAlertId, getDismissedAlertIds } from './alert-dismissal-store';

// Back-off for a failed initial hub start (see startWithRetry); the last value repeats.
const HUB_START_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 30_000];

// Site-wide live-alert state for the real-time toast stack (DEV-1165 Phase 2) — REST for the
// initial snapshot / reconnect resync, SignalR for live push, following the hybrid pattern in
// the spec: REST alone would miss anything pushed while disconnected, SignalR alone would miss
// anything that happened before the client connected (the "cold start" problem).
@Injectable({ providedIn: 'root' })
export class LiveAlertsService {
  private http = inject(HttpClient);
  private msalService = inject(MsalService);

  private readonly liveAlertsSubject = new BehaviorSubject<Alert[]>([]);
  readonly liveAlerts$: Observable<Alert[]> = this.liveAlertsSubject.asObservable();

  // Same live set as liveAlertsSubject above, but never filtered by *this browser's own*
  // sessionStorage dismissals — a viewer closing their own toast is a purely local/personal
  // action and must not make a system-wide count (e.g. the Alerts admin header's "N live
  // alerts" badge) look lower than what's actually live for everyone else.
  private readonly allLiveAlertsSubject = new BehaviorSubject<Alert[]>([]);
  readonly liveAlertCount$: Observable<number> = this.allLiveAlertsSubject.pipe(map(alerts => alerts.length));

  private hubConnection: signalR.HubConnection | null = null;

  // Idempotent — app.component.ts calls this once per LOGIN_SUCCESS/SSO_SILENT_SUCCESS event,
  // which can fire more than once in a session (e.g. silent token refresh).
  connect(): void {
    if (this.hubConnection) {
      return;
    }

    this.syncFromRest();
    this.startHubConnection();
  }

  dismiss(alertId: number): void {
    dismissAlertId(alertId);
    this.liveAlertsSubject.next(this.liveAlertsSubject.value.filter(alert => alert.id !== alertId));
  }

  private syncFromRest(): void {
    this.http.get<Alert[]>(`${environment.apiUrl}/alerts/live`).subscribe({
      next: alerts => {
        const dismissed = getDismissedAlertIds();
        this.liveAlertsSubject.next(alerts.filter(alert => !dismissed.has(alert.id)));
        this.allLiveAlertsSubject.next(alerts);
      },
      // A transient REST failure (e.g. offline) shouldn't wipe whatever toasts are already
      // showing — just log it; the next reconnect/resync attempt will retry.
      error: error => console.error('Failed to sync live alerts.', error)
    });
  }

  private startHubConnection(): void {
    const hubUrl = environment.apiUrl.replace(/\/api\/?$/, '') + '/hubs/alerts';

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.acquireToken(),
        // We authenticate purely via the bearer token above, not cookies — the SignalR client
        // sends credentials (cookies) by default, which forces the browser to require a
        // non-wildcard CORS origin + Access-Control-Allow-Credentials on the negotiate request.
        // The backend's CORS policy uses AllowAnyOrigin() with no AllowCredentials(), so that
        // preflight was failing outright; disabling credentials here avoids needing to touch
        // the backend's CORS config at all.
        withCredentials: false
      })
      .withAutomaticReconnect()
      // The client's default (Information) logs the full WebSocket URL — including the
      // access_token query parameter SignalR has to use for WebSockets — to every user's console.
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.hubConnection.on('AlertUpserted', (alert: Alert) => this.applyUpsert(alert));
    this.hubConnection.on('AlertRemoved', (alertId: number) => this.applyRemoval(alertId));

    // Per the spec's deployment-resilience table: on reconnect (after a backend restart or a
    // network blip), re-sync via REST rather than trusting the connection gap contained nothing.
    this.hubConnection.onreconnected(() => this.syncFromRest());

    this.startWithRetry(0);
  }

  // withAutomaticReconnect() only covers a connection that was established and then dropped — a
  // failed *initial* start() (backend mid-deploy, a transient token failure) is never retried by
  // the client itself, which would leave this tab without live pushes until a full reload.
  private startWithRetry(attempt: number): void {
    const connection = this.hubConnection;
    if (!connection) {
      return;
    }

    connection
      .start()
      .then(() => {
        // Anything pushed while we were failing to connect was missed — same resync as
        // onreconnected. The first attempt doesn't need it: connect() already synced.
        if (attempt > 0) {
          this.syncFromRest();
        }
      })
      .catch(error => {
        const delay = HUB_START_RETRY_DELAYS_MS[Math.min(attempt, HUB_START_RETRY_DELAYS_MS.length - 1)];
        console.error(`Failed to connect to AlertsHub, retrying in ${delay / 1000}s.`, error);
        setTimeout(() => this.startWithRetry(attempt + 1), delay);
      });
  }

  private applyUpsert(alert: Alert): void {
    // A pushed upsert is always a fresh admin action (create, edit, or a toggle/schedule
    // activating it) — a dismissal from an earlier viewing is now stale and must be evicted so
    // the alert reappears, per DEV-1165's "Session Storage Eviction for Reactivated & Modified
    // Alerts" rule. Reaches every connected client the same way, including the acting admin's
    // own tab (the hub broadcasts to Clients.All, not Clients.Others), so this one path covers
    // both "my own edit" and "someone else's live edit" without a separate local code path.
    evictDismissedAlertId(alert.id);

    const current = this.liveAlertsSubject.value;
    const next = current.some(existing => existing.id === alert.id)
      ? current.map(existing => (existing.id === alert.id ? alert : existing))
      : [...current, alert];
    this.liveAlertsSubject.next(next);

    const currentAll = this.allLiveAlertsSubject.value;
    const nextAll = currentAll.some(existing => existing.id === alert.id)
      ? currentAll.map(existing => (existing.id === alert.id ? alert : existing))
      : [...currentAll, alert];
    this.allLiveAlertsSubject.next(nextAll);
  }

  private applyRemoval(alertId: number): void {
    this.liveAlertsSubject.next(this.liveAlertsSubject.value.filter(alert => alert.id !== alertId));
    this.allLiveAlertsSubject.next(this.allLiveAlertsSubject.value.filter(alert => alert.id !== alertId));
  }

  // SignalR needs the token explicitly (a hub connection doesn't ride through MsalInterceptor
  // the way plain HttpClient calls do) — same scopes already used for the REST API calls.
  private async acquireToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return '';
    }

    const result = await this.msalService.instance.acquireTokenSilent({
      scopes: environment.apiConfig.scopes,
      account
    });
    return result.accessToken;
  }
}
